import React, { useMemo } from 'react';
import { AlertTriangle, ChevronRight, Lightbulb, X } from 'lucide-react';
import type { LstDiaRaioX } from '../../services/lstAdherence';
import type { AdherenceLossParetoItem } from '../../services/adherenceLossDiagnostic';
import { buildDrawerPrescriptiveAction } from '../../services/cadeiaChainInsights';
import {
  GRAVIDADE_LABEL,
  GRAVIDADE_STYLE,
  resolveDrawerGravidade,
} from '../../services/cadeiaSeverityStyles';
import type { TplOeeMetrics } from '../../services/lstOeeMetrics';
import type { EnterpriseAction } from '../../../../services/actionService';
import { ACTION_STATUS_COLORS, ACTION_STATUS_LABELS } from '../../../../services/actionService';
import { KamishibaiMatrixCell } from '../kamishibai/KamishibaiSymbol';
import { ProcessStoppedBadge } from '../kamishibai/ProcessStoppedControl';

interface AdherenceLossDiagnosticDrawerProps {
  open: boolean;
  onClose: () => void;
  memberName: string;
  date: string;
  oee: TplOeeMetrics;
  pareto: AdherenceLossParetoItem[];
  correctiveActions: EnterpriseAction[];
  raioX: LstDiaRaioX | null;
  loadingActions?: boolean;
  onOpenAction?: (actionId: string) => void;
}

const WEEKDAY_FULL = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

function formatLongDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${WEEKDAY_FULL[date.getDay()]}, ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

/** Drawer executivo — diagnóstico de perda de aderência. */
export const AdherenceLossDiagnosticDrawer: React.FC<AdherenceLossDiagnosticDrawerProps> = ({
  open,
  onClose,
  memberName,
  date,
  oee,
  pareto,
  correctiveActions,
  raioX,
  loadingActions,
  onOpenAction,
}) => {
  const hasLoss = oee.counts.naoRealizado > 0 || oee.counts.desvio > 0;
  const topCause = pareto[0]?.label ?? null;
  const hasCriticalLoss = (raioX?.detalhes ?? []).some(
    (d) => d.status === 'nao_realizado' && d.template.is_critical,
  );

  const gravidade = resolveDrawerGravidade(
    oee.counts.naoRealizado,
    oee.counts.desvio,
    hasCriticalLoss,
  );
  const gStyle = GRAVIDADE_STYLE[gravidade];

  const prescriptive = useMemo(
    () =>
      buildDrawerPrescriptiveAction(
        topCause,
        correctiveActions.length > 0,
        hasCriticalLoss,
        oee.counts.naoRealizado,
        oee.counts.desvio,
      ),
    [topCause, correctiveActions.length, hasCriticalLoss, oee.counts.naoRealizado, oee.counts.desvio],
  );

  const whatHappened = useMemo(() => {
    const parts: string[] = [];
    if (oee.counts.naoRealizado > 0) parts.push(`${oee.counts.naoRealizado} não realizado(s)`);
    if (oee.counts.desvio > 0) parts.push(`${oee.counts.desvio} desvio(s)`);
    if (parts.length === 0) return 'Sem perdas registradas neste dia.';
    return parts.join(' e ');
  }, [oee]);

  const lossRoutines = useMemo(
    () => (raioX?.detalhes ?? []).filter((d) => d.status !== 'realizado'),
    [raioX],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
        onClick={onClose}
        aria-label="Fechar diagnóstico"
      />
      <aside className="relative flex h-full w-full max-w-[420px] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-[var(--bg-surface)]">
        <header className={`shrink-0 border-b px-3 py-2 ${gStyle.border} bg-white dark:bg-[var(--bg-surface)]`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5">
                <p className={`text-[9px] font-bold uppercase tracking-wide ${gStyle.header}`}>
                  Diagnóstico Executivo
                </p>
                <span className={`rounded px-1.5 py-0.5 text-[8px] font-bold uppercase ${gStyle.badge}`}>
                  {GRAVIDADE_LABEL[gravidade]}
                </span>
              </div>
              <h2 className="text-[14px] font-bold text-slate-900 dark:text-slate-50">{memberName}</h2>
              <p className="text-[10px] text-slate-500">{formatLongDate(date)}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-2">
          <DiagBlock title="O que aconteceu?" icon={AlertTriangle} gravidade={gravidade}>
            <p className="text-[11px] leading-snug text-slate-700 dark:text-slate-300">{whatHappened}</p>
            <p className="mt-1 text-[9px] tabular-nums text-slate-400">
              Disciplina {oee.disciplinaPct}% · Conformidade {oee.conformidadePct}%
            </p>
          </DiagBlock>

          <DiagBlock title="Causa principal" icon={AlertTriangle} gravidade="atencao">
            {topCause ? (
              <p className="text-[11px] font-medium text-slate-800 dark:text-slate-100">{topCause}</p>
            ) : (
              <p className="text-[10px] text-slate-400">Sem causa dominante identificada.</p>
            )}
            {pareto.length > 1 && (
              <ul className="mt-1 space-y-0.5">
                {pareto.slice(1, 4).map((p) => (
                  <li key={p.key} className="flex justify-between text-[9px] text-slate-500">
                    <span className="truncate pr-2">{p.label}</span>
                    <span className="shrink-0 tabular-nums">{p.pct}%</span>
                  </li>
                ))}
              </ul>
            )}
          </DiagBlock>

          <PrescriptiveActionBlock prescriptive={prescriptive} />

          {hasLoss && lossRoutines.length > 0 && (
            <DiagBlock title="Rotinas com perda" icon={AlertTriangle} gravidade={gravidade}>
              <ul className="max-h-[160px] space-y-0.5 overflow-y-auto">
                {lossRoutines.map((d) => {
                  const itemGrav =
                    d.status === 'nao_realizado' || d.status === 'processo_parado'
                      ? 'critico'
                      : 'atencao';
                  const itemStyle = GRAVIDADE_STYLE[itemGrav];
                  return (
                    <li
                      key={d.template.id}
                      className={`flex items-center justify-between gap-2 rounded border px-2 py-1 ${itemStyle.border}`}
                    >
                      <span className="min-w-0 truncate text-[10px] font-medium text-slate-700 dark:text-slate-200">
                        {d.template.title}
                        {d.template.is_critical && (
                          <span className="ml-1 text-[8px] font-bold text-red-600">Crítica</span>
                        )}
                      </span>
                      {d.status === 'processo_parado' ? (
                        <ProcessStoppedBadge size="sm" />
                      ) : (
                        <KamishibaiMatrixCell status={d.status} />
                      )}
                    </li>
                  );
                })}
              </ul>
            </DiagBlock>
          )}

          {(loadingActions || correctiveActions.length > 0) && (
            <DiagBlock title="Planos corretivos">
              {loadingActions ? (
                <p className="text-[10px] text-slate-400">Carregando…</p>
              ) : (
                <ul className="space-y-1">
                  {correctiveActions.slice(0, 4).map((action) => (
                    <li
                      key={action.id}
                      className="rounded border border-slate-100 px-2 py-1 dark:border-slate-800"
                    >
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-[10px] font-semibold text-slate-800 dark:text-slate-100">
                          {action.title}
                        </p>
                        <span
                          className={`shrink-0 rounded px-1 py-0.5 text-[8px] font-bold ${ACTION_STATUS_COLORS[action.status]}`}
                        >
                          {ACTION_STATUS_LABELS[action.status]}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center justify-between">
                        <span className="text-[9px] text-slate-400">
                          {action.assigned_name ?? 'Sem responsável'}
                        </span>
                        {onOpenAction && (
                          <button
                            type="button"
                            onClick={() => onOpenAction(action.id)}
                            className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-blue-600 hover:underline"
                          >
                            Abrir <ChevronRight size={9} />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </DiagBlock>
          )}
        </div>
      </aside>
    </div>
  );
};

function PrescriptiveActionBlock({
  prescriptive,
}: {
  prescriptive: {
    whatToDo: string;
    responsible: string;
    reason: string;
    suggestedDeadline: string;
    gravidade: 'normal' | 'atencao' | 'critico';
  };
}) {
  const gStyle = GRAVIDADE_STYLE[prescriptive.gravidade];
  const bgCls =
    prescriptive.gravidade === 'critico'
      ? 'bg-red-50/80 dark:bg-red-950/25'
      : prescriptive.gravidade === 'atencao'
        ? 'bg-amber-50/80 dark:bg-amber-950/20'
        : 'bg-blue-50/60 dark:bg-blue-950/15';

  return (
    <section className={`rounded-lg border-2 p-3 ${gStyle.border} ${bgCls}`}>
      <h3 className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
        <Lightbulb size={14} className="text-[#2F6FED]" />
        Ação sugerida
        <span className={`ml-auto rounded px-1.5 py-0.5 text-[8px] font-bold uppercase ${gStyle.badge}`}>
          {GRAVIDADE_LABEL[prescriptive.gravidade]}
        </span>
      </h3>
      <p className="mb-2 text-[13px] font-bold leading-snug text-slate-900 dark:text-slate-50">
        {prescriptive.whatToDo}
      </p>
      <dl className="space-y-1.5 text-[11px]">
        <PrescRow label="Responsável" value={prescriptive.responsible} />
        <PrescRow label="Motivo" value={prescriptive.reason} />
        <PrescRow label="Prazo" value={prescriptive.suggestedDeadline} bold />
      </dl>
    </section>
  );
}

function PrescRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="w-[88px] shrink-0 font-semibold text-slate-500">{label}</dt>
      <dd className={`min-w-0 text-slate-700 dark:text-slate-300 ${bold ? 'font-bold' : ''}`}>{value}</dd>
    </div>
  );
}

function DiagBlock({
  title,
  icon: Icon,
  gravidade,
  children,
}: {
  title: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  gravidade?: 'normal' | 'atencao' | 'critico';
  children: React.ReactNode;
}) {
  const border =
    gravidade && gravidade !== 'normal'
      ? GRAVIDADE_STYLE[gravidade].border
      : 'border-slate-200 dark:border-slate-700';

  return (
    <section className={`rounded-lg border p-2 ${border}`}>
      <h3 className="mb-1 flex items-center gap-1 text-[9px] font-bold uppercase text-slate-500">
        {Icon && (
          <Icon
            size={11}
            className={gravidade === 'critico' ? 'text-red-500' : gravidade === 'atencao' ? 'text-amber-500' : 'text-[#2F6FED]'}
          />
        )}
        {title}
      </h3>
      {children}
    </section>
  );
}
